import * as React from 'react';
import { Box } from '@amzn/awsui-components-react-v3';
import { getArgoConfig } from '../../window';
import { get as _get } from 'lodash';
import { credentialStorage } from '../../utils/CredentialStorage';
import KatalMetricsPublisher from '@katal/metrics/lib/KatalMetricsPublisher';
import { makePublisher } from '../../utils/metrics/metrics';
import { metricConfig, metricServiceName } from '../../utils/metrics/constant';
import './navbar.scss';

interface NavigationProps {
  isLandingPage?: boolean;
  navigationMenu: JSX.Element | JSX.IntrinsicElements | null;
  onAppClick?: (app: NavigableApplicationsObject) => Promise<void>;
}
interface NavigationHeaderProps {
  isApplication: boolean;
}
interface NavigableApplicationsObject {
  navTitle: string;
  navLink: string;
  allowsExternalUsers: boolean;
}

const navMetricPublisher = makePublisher(metricConfig, metricServiceName);

function sendSwitchAppMetric(metricsPublisher: KatalMetricsPublisher, apiName: string, value: number) {
  metricsPublisher.newChildActionPublisherForMethod('ArgoSwitchingApp').publishCounter(apiName, value);
}

export function Navigation({ isLandingPage = false, navigationMenu, onAppClick }: NavigationProps) {
  const isLandingPageApp = isLandingPage;
  const config = getArgoConfig();
  const navigableApplications = _get(config, 'argo.navigableApplications', []);
  const isExternalUser = credentialStorage.isExternalUser();

  const tvpLink = _get(config, 'argo.tvpLink', '');
  const tvpLinkText = 'Titan Vendor Portal';
  const enableTVPLinkForExternalUser = _get(config, 'argo.enableTVPLinkForExternalUser', false);

  const handleApplicationLinkClick = React.useCallback((app: NavigableApplicationsObject) => {
    return async (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (onAppClick) {
        event.preventDefault();
        try {
          await onAppClick(app);
          window.location.href = app.navLink;
        } catch (error) {
          console.error('Error updating recent apps:', error);
          window.location.href = app.navLink;
        }
      }
    };
  }, [onAppClick]);

  // Only used for LandingPage App
  const applicationLinks: React.ReactElement[] = [];
  navigableApplications.forEach((app: NavigableApplicationsObject) => {
    // External Users should not see links for apps that do not allow External Users
    if (isExternalUser && !app.allowsExternalUsers) {
      return;
    }

    applicationLinks.push(
      <div key={applicationLinks.length}>
        <li>
          <a
            onClick={handleApplicationLinkClick(app)}
            style={{ cursor: 'pointer' }}
            >
            {app.navTitle}
          </a>
        </li>
      </div>
    );
  });

  // Add tvpLink only if there is a TVP link present in argo-config
  if (tvpLink) {
    // If 'enableTVPLinkForExternalUser' flag is true then the link should be shown only for external users
    // If 'enableTVPLinkForExternalUser' is false, then link should be shown only for internal users
    if ((enableTVPLinkForExternalUser && isExternalUser) || (!enableTVPLinkForExternalUser && !isExternalUser)) {
      const tvpApp: NavigableApplicationsObject = {
        navTitle: tvpLinkText,
        navLink: tvpLink,
        allowsExternalUsers: enableTVPLinkForExternalUser
      };

      applicationLinks.push(
        <div key={applicationLinks.length}>
          <li>
            <a
              onClick={handleApplicationLinkClick(tvpApp)}
              style={{ cursor: 'pointer' }}
            >
              {tvpLinkText}
            </a>
          </li>
        </div>
      );
    }
  }

  // Only used for LandingPage App
  const navigationStructure =
    <div className="navigation_content">
      <div className="awsui-side-navigation">
        {applicationLinks}
      </div>
    </div>

  return (
    <Box className='argocore_navigation'>
      <NavigationHeader isApplication={!isLandingPageApp} />
      {isLandingPage ? navigationStructure : navigationMenu}
    </Box>
  );
}

export function NavigationHeader(props: NavigationHeaderProps) {

  return (
    <div className='navigation_header'>
      <div className='navHeader'>
        <a href='/' className='navHomeLink'>
          <h1>Infra Portal</h1>
        </a>
      </div>
      <div className='switchApp'>
        {props.isApplication && <a href='/' onClick={() => sendSwitchAppMetric(navMetricPublisher, 'ArgoSwitchApp', 1)} >Switch App</a>}
      </div>
    </div>
  );
}
